'use client';

import { NavArrowLeft } from '@byway/icons/src/icons/NavArrowLeft';
import { useUser } from '@clerk/nextjs';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import Script from 'next/script';
import { useTranslations } from 'next-intl';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { ErrorMessage } from '~/components/Common/ErrorMessage';
import JourneyBookingCTA from '~/components/Journey/JourneyBookingCTA';
import JourneyDescriptionMobile from '~/components/Journey/JourneyDescriptionMobile';
import JourneyHeader from '~/components/Journey/JourneyHeader';
import JourneyIncludes from '~/components/Journey/JourneyIncludes';
import JourneyIntro from '~/components/Journey/JourneyIntro';
import JourneyLastMinutePremiumAlert from '~/components/Journey/JourneyLastMinutePremiumAlert';
import JourneyMobileCTABar from '~/components/Journey/JourneyMobileCTABar';
import JourneySecureBooking from '~/components/Journey/JourneySecureBooking';
import JourneyTopBar from '~/components/Journey/JourneyTopBar';
import { JourneyUnavailabilityBanner } from '~/components/Journey/JourneyUnavailabilityBanner';
import { MajorErrorBanner } from '~/components/Journey/MajorErrorBanner/MajorErrorBanner';
import { OriginDateShiftNotice } from '~/components/Journey/OriginDateShiftNotice';
import { PriceBar } from '~/components/Journey/PriceBar/PriceBar';
import { SaveTripPrompt } from '~/components/Journey/SaveTripToast';
import { StickyTripHeader } from '~/components/Journey/StickyTripHeader';
import { JourneyMessagingDesktop, JourneyMessagingMobile } from '~/components/JourneyMessaging/JourneyMessaging';
import { Box } from '~/components/Layout/Box';
import { Link } from '~/components/Link';
import { DiyOptionBlock, type DiyOptionBlockData } from '~/components/marketing/DiyOptionBlock';
import { TRPC_SETTINGS } from '~/constants/trpc';
import JourneyDataContext from '~/contexts/JourneyDataContext';
import { useScrollToTopOnLoad } from '~/hooks/useScrollToTopOnLoad';
import useSendFacebookPageViewEvent from '~/hooks/useSendFacebookPageViewEvent';
import { useUnavailableDates } from '~/hooks/useUnavailableDates';
import { api } from '~/libs/api';
import type { UserPayload } from '~/server/journey/buildUserPayload';
import { JourneyStoreInitializer, useJourneyActions } from '~/stores/journeyStore';
import type { JourneyTransformed } from '~/types/Journey.types';
import { ensureFutureDateString } from '~/utils/dateHelpers/dateHelpers';
import { JourneyBpiLinkBanner } from './JourneyBpiLinkBanner/JourneyBpiLinkBanner';
import styles from './JourneyDetails.module.css';
const JourneyItinerary = dynamic(() => import('~/components/Journey/JourneyItinerary'));
const JourneyMapForTrip = dynamic(() => import('./JourneyMap').then(mod => ({
  default: mod.JourneyMapForTrip
})));
const AlternateRegionsCarousel = dynamic(() => import('~/components/AlternateRegionsCarousel').then(mod => ({
  default: mod.AlternateRegionsCarousel
})));
const TrustBox = dynamic(() => import('~/components/Trustpilot').then(mod => ({
  default: mod.TrustBox
})));
const JourneyUpdateTransportClass = dynamic(() => import('~/components/Journey/JourneyUpdateTransportClass'));
interface JourneyDetailsProps {
  user: UserPayload;
  journeyId: string;
  source?: string;
  journey?: JourneyTransformed;
  shouldHidePriceInitially?: boolean;
  loyaltyDiscount?: number;
  loyaltyTierId?: string;
  loyaltyTierName?: string;
  diyOptionBlock?: DiyOptionBlockData | null;
}
const BING_TRACKING_SCRIPT = `window.uetq = window.uetq || [];window.uetq.push("event", "trip-page-book-or-enquire-click", {"currency":"USD"});`;
export function JourneyDetails({
  user,
  journeyId,
  journey,
  shouldHidePriceInitially,
  loyaltyDiscount = 0,
  loyaltyTierId,
  loyaltyTierName,
  diyOptionBlock
}: Readonly<JourneyDetailsProps>) {
  const pathname = usePathname();
  const t = useTranslations('journey');
  useSendFacebookPageViewEvent();
  useScrollToTopOnLoad();
  const [unavailableDates, setUnavailableDates] = useUnavailableDates();
  const hasScrolledToTop = useRef(false);
  const {
    user: authUser
  } = useUser();
  const {
    setIsCheckingAvailability,
    setJourneyEditErrors
  } = useJourneyActions();
  const {
    alternateRegions,
    source: journeySource,
    bookingInformation,
    overrideEditableTrip,
    details
  } = journey as JourneyTransformed || {
    alternateRegions: undefined,
    source: undefined,
    bookingInformation: undefined,
    overrideEditableTrip: undefined,
    details: undefined
  };
  const creationSource = journeySource?.creationSource ?? '';
  const showDiyBlock = journey?.createdBy === 'anonymousUser';
  const isBlueprintJourney = useMemo(() => creationSource === 'FROM_TEMPLATE' || creationSource === 'FROM_VIRTUAL_BLUEPRINT' || creationSource === 'FROM_VIRTUAL_BLUEPRINT_TEMPLATE', [creationSource]);
  const currentFuturePricingTiers = isBlueprintJourney;
  const currentUnavailableDates = useMemo(() => isBlueprintJourney && unavailableDates && 'blueprintId' in unavailableDates && journeySource?.previousIds?.includes(unavailableDates.blueprintId), [isBlueprintJourney, unavailableDates, journeySource?.previousIds]);
  useEffect(() => {
    if (shouldHidePriceInitially) {
      setIsCheckingAvailability(true);
    } else if (journeyId) {
      setIsCheckingAvailability(false);
    }
  }, [journeyId, shouldHidePriceInitially, setIsCheckingAvailability]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const devShowMajorErrorBanner = params.get('devMajorError') === '1' && process.env.VERCEL_ENV !== 'production';
    if (devShowMajorErrorBanner) {
      setJourneyEditErrors(['UNKNOWN_FAILURE']);
    }
  }, [setJourneyEditErrors]);
  useLayoutEffect(() => {
    if (!hasScrolledToTop.current) {
      window.scrollTo({
        top: 0,
        behavior: 'instant'
      });
      hasScrolledToTop.current = true;
    }
  }, []);
  useEffect(() => {
    if (!currentFuturePricingTiers) {
      sessionStorage.removeItem('futurePricingTiers');
    }
    if (!currentUnavailableDates && unavailableDates) {
      sessionStorage.removeItem('unavailableDates');
      setUnavailableDates(null);
    }
  }, [currentFuturePricingTiers, currentUnavailableDates, unavailableDates, setUnavailableDates]);
  const {
    data: isTripSaved
  } = api.account.isTripSaved.useQuery({
    tripId: journeyId
  }, {
    enabled: !!authUser && !!journeyId,
    staleTime: 5 * 60 * 1000,
    ...TRPC_SETTINGS.QUERY_NO_REFETCH
  });
  const tripInDaysDetails = useMemo(() => ({
    tripInDays: details?.tripInDays ?? 0
  }), [details?.tripInDays]);
  const isBookedJourney = useMemo(() => bookingInformation?.bookingStatus === 'BOOKED' || bookingInformation?.bookingStatus === 'OUTSTANDING', [bookingInformation?.bookingStatus]);
  const isPlaygroundJourney = creationSource.includes('PLAYGROUND');
  const cheapestAvailableDate = details?.cheapestAvailableDate;
  const futureDatesPriceTiered = details?.futureDatesPriceTiered;
  const cheapestAvailableDateUpdated = details?.cheapestAvailableDateUpdated;

  // Guard against startDate that is in the past - use default if in past
  const startDateToCheck = useMemo(() => cheapestAvailableDate?.startDateUS || cheapestAvailableDate?.startDate, [cheapestAvailableDate?.startDateUS, cheapestAvailableDate?.startDate]);
  const futurePricingTiers = useMemo(() => cheapestAvailableDate && futureDatesPriceTiered && journey ? {
    blueprintId: journey.uniqueId,
    cheapestAvailableDate: ensureFutureDateString(startDateToCheck),
    pricingTiers: futureDatesPriceTiered,
    cheapestAvailableDateUpdated: cheapestAvailableDateUpdated
  } : null, [cheapestAvailableDate, futureDatesPriceTiered, journey?.uniqueId, startDateToCheck, cheapestAvailableDateUpdated]);
  const isEditable = useMemo(() => typeof overrideEditableTrip === 'boolean' ? overrideEditableTrip : !isBookedJourney && (isBlueprintJourney || isPlaygroundJourney), [overrideEditableTrip, isBookedJourney, isBlueprintJourney, isPlaygroundJourney]);
  const contextValue = useMemo(() => ({
    user,
    journeyType: 'JOURNEY' as const,
    urlPath: pathname,
    futurePricingTiers: currentFuturePricingTiers ? futurePricingTiers : null,
    unavailableDates: currentUnavailableDates && unavailableDates && 'blueprintId' in unavailableDates ? unavailableDates : undefined,
    loyaltyDiscount,
    loyaltyTierId,
    loyaltyTierName
  }), [user, pathname, currentFuturePricingTiers, futurePricingTiers, currentUnavailableDates, unavailableDates, loyaltyDiscount, loyaltyTierId, loyaltyTierName]);
  if (!journey) {
    return <ErrorMessage />;
  }
  return <JourneyStoreInitializer journey={journey}>
      <JourneyDataContext.Provider value={contextValue}>
        <div>
          <Script id="bing-page-tracking" strategy="afterInteractive">
            {BING_TRACKING_SCRIPT}
          </Script>
          <div className="sticky top-0 z-[999] bg-white pb-4">
            <div className="bg-blue-100">
              <div className="container mx-auto md:px-8">
                <JourneyTopBar isEditable={isEditable} />
              </div>
            </div>
            <div className="container mx-auto md:px-8">
              <OriginDateShiftNotice journeyId={journeyId} />
              <PriceBar />
            </div>
          </div>
          <StickyTripHeader />
          <SaveTripPrompt />
          <div className="container px-1 mx-auto md:px-8">
            <JourneyMessagingDesktop />
            <JourneyUnavailabilityBanner />
            <JourneyIncludes variant="journey" details={tripInDaysDetails} uniqueId={journey.uniqueId} />
            {isTripSaved && <Link href="/account" target="_self">
                <div className="flex items-center px-2 mt-4 mb-4 typography-body-2 md:mb-0 md:px-0 md:mt-8">
                  <NavArrowLeft decorative />
                  {t('back_to_account')}
                </div>
              </Link>}
            <JourneyLastMinutePremiumAlert />
            <MajorErrorBanner />
            <JourneyHeader />
            <JourneyMobileCTABar isEditable={isEditable} />
            <JourneyDescriptionMobile />
            <JourneyMessagingMobile />
            <JourneyIncludes variant="journey-mobile" details={tripInDaysDetails} uniqueId={journey.uniqueId} />
            <JourneyIntro />
            <JourneyMapForTrip />
            <hr className="mx-4 my-8 md:mx-0" />
            {journey.allowTransportClassUpgrade && <div className="max-w-screen-lg mx-auto">
                <JourneyUpdateTransportClass />
              </div>}
            {showDiyBlock && diyOptionBlock && <div className={styles.diyOptionBlock}>
                <DiyOptionBlock data={diyOptionBlock} />
              </div>}
            <JourneyItinerary />
            {showDiyBlock && diyOptionBlock && <div className={styles.diyOptionBlock}>
                <DiyOptionBlock data={diyOptionBlock} />
              </div>}
            <JourneyBpiLinkBanner />
            <div className="md:space-y-8 md:mb-8">
              <JourneyBookingCTA />
              {alternateRegions && alternateRegions?.length > 0 && <AlternateRegionsCarousel alternateRegions={alternateRegions} />}
              <JourneySecureBooking />
              <Box space="--by-space-800">
                <TrustBox templateId="539ad0ffdec7e10e686debd7" />
              </Box>
            </div>
          </div>
        </div>
      </JourneyDataContext.Provider>
    </JourneyStoreInitializer>;
}